import { FolderOpen, LogOut, ShieldCheck } from 'lucide-react';
import type { ReactElement } from 'react';

import { useSession } from '../auth.js';
import { Button, LinkCard, Segmented } from '../design/components.js';

import { DomainHeader, HubBlocs, TodoList } from './DomainPage.js';
import { useShell } from './Shell.js';
import { domainTodo, hubOf, RUBRIQUES_MAX, rubriquesEnTete } from './navigation.js';
import { useMesRubriques } from './rubriques.js';
import { useTheme, type ThemeChoice } from './theme.js';

/**
 * Mon compte (avatar de l'en-tête, option B) : Ma fiche, Mes échéances, Mes notifications,
 * Oasis'Call (selon la fonction), compte et sécurité, apparence, déconnexion — comme
 * « Mon compte » de l'intranet. Pas de menu déroulant.
 */
const THEMES: readonly { value: ThemeChoice; label: string }[] = [
  { value: 'auto', label: 'Système' },
  { value: 'light', label: 'Clair' },
  { value: 'dark', label: 'Sombre' },
];

export function MonComptePage(): ReactElement {
  const { shell, gestion } = useShell();
  const mesRubriques = useMesRubriques();
  const enTete = rubriquesEnTete(gestion, shell.navigation, mesRubriques.epinglees);
  const affichees = new Set<string>(enTete.visibles.map((d) => d.key));
  const { signOut } = useSession();
  const theme = useTheme();
  const systeme = theme.choix === 'auto' ? (theme.effectif === 'dark' ? 'sombre' : 'clair') : null;
  const blocs = hubOf('compte', shell.navigation).map((b) => ({
    ...b,
    title: 'Ma fiche et mes suivis',
  }));
  const todo = domainTodo('compte', shell.tiles);
  return (
    <div className="o-stack o-hub">
      <DomainHeader domaine="compte" />
      {todo.length > 0 && <TodoList tiles={todo} label="À traiter" />}
      <HubBlocs blocs={blocs} />
      <section className="o-hub-bloc" aria-labelledby="bloc-compte">
        <h2 id="bloc-compte" className="o-hub-bloc__title">
          Préférences et sécurité
        </h2>
        <div className="o-link-grid">
          <LinkCard
            title="Compte et sécurité"
            description="Identité, second facteur, notifications sur ce navigateur."
            icon={ShieldCheck}
            to="/compte"
          />
          {(shell.liens ?? []).map((l) => (
            <article key={l.key} className="o-link-card">
              <a
                className="o-link-card__main"
                href={l.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                <span className="o-link-card__icon" aria-hidden="true">
                  <FolderOpen size={20} />
                </span>
                <span className="o-link-card__text">
                  <span className="o-link-card__title">{l.label}</span>
                  <span className="o-link-card__description">
                    Mes fichiers sur Nextcloud, dans un nouvel onglet.
                  </span>
                </span>
              </a>
            </article>
          ))}
        </div>
        <div className="o-field" style={{ marginTop: 'var(--space-4)' }}>
          <span style={{ fontWeight: 650, fontSize: 'var(--text-sm)' }}>Thème de l'affichage</span>
          <Segmented<ThemeChoice>
            label="Thème de l'affichage"
            options={THEMES}
            value={theme.choix}
            onChange={theme.choisir}
          />
          <p className="o-hint" aria-live="polite">
            {systeme === null
              ? 'Choisi pour ce navigateur, quel que soit le réglage de l’appareil.'
              : `Suit le réglage de l’appareil : thème ${systeme} en ce moment.`}
          </p>
        </div>
        {gestion.length > RUBRIQUES_MAX && (
          <fieldset
            className="o-field"
            style={{ marginTop: 'var(--space-4)', border: 0, padding: 0 }}
          >
            <legend style={{ fontWeight: 650, fontSize: 'var(--text-sm)' }}>
              Mes rubriques dans l'en-tête de la gestion
            </legend>
            {gestion.map((d) => (
              <label key={d.key} className="o-check" style={{ marginBottom: 'var(--space-1)' }}>
                <input
                  type="checkbox"
                  checked={affichees.has(d.key)}
                  onChange={(e) => {
                    const suivant = new Set(affichees);
                    if (e.target.checked) suivant.add(d.key);
                    else suivant.delete(d.key);
                    if (suivant.size === 0) return; // au moins une rubrique dans l'en-tête
                    mesRubriques.choisir(gestion.map((g) => g.key).filter((k) => suivant.has(k)));
                  }}
                />{' '}
                {d.label}
              </label>
            ))}
            <p className="o-hint">
              {mesRubriques.epinglees === null
                ? `Par défaut : les ${String(RUBRIQUES_MAX)} rubriques où vous avez le plus d’écrans ; les autres sont dans « Autres ». Choix gardé sur ce navigateur.`
                : 'Votre choix, gardé sur ce navigateur ; les autres rubriques sont dans « Autres ».'}
            </p>
            {mesRubriques.epinglees !== null && (
              <div>
                <Button
                  onClick={() => {
                    mesRubriques.choisir(null);
                  }}
                >
                  Revenir au choix par défaut
                </Button>
              </div>
            )}
          </fieldset>
        )}
        <div className="o-hub-actions" style={{ marginTop: 'var(--space-4)' }}>
          <Button onClick={() => void signOut()}>
            <LogOut aria-hidden="true" size={18} /> Se déconnecter
          </Button>
        </div>
      </section>
    </div>
  );
}
